/**
 * OneDrive list API slice. State: value, loading, isError, message.
 * Thunk only; no legacy action dispatches. Filtered slice listens to fulfilled.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getOneDriveDocs } from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import CustomerConfig from "../../../azure/CustomerConfig";
import { logger } from "../../../utils/logger";

const userAuth = new UserAuth();

export const fetchOneDriveDocuments = createAsyncThunk(
  "oneDriveList/fetch",
  async (
    {
      teamId,
      channelId,
      tenantId,
      pageSize,
      searchText,
      documentType,
      fileRoot,
      isOnedrive,
    },
    { rejectWithValue }
  ) => {
    logger.info("fetchOneDriveDocuments start", { teamId, channelId }, "REDUX");
    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("fetchOneDriveDocuments no access token", tokenResponse, "REDUX");
      return rejectWithValue({ message: "No access token" });
    }

    const config = new CustomerConfig();
    const recSize = pageSize ?? config.getPageLimit();

    try {
      const { data, status } = await getOneDriveDocs(accessToken, {
        teamId: teamId ?? "",
        channelId: channelId ?? "",
        tenantId: tenantId ?? "",
        fileType: documentType ?? "",
        searchText: searchText ?? "",
        recFrom: 0,
        recSize,
        oneDriveRoot: fileRoot ?? "",
        isOnedrive: isOnedrive ?? false,
      });

      if (status === 200) {
        const value = data?.value != null ? data.value : [];
        const list = Array.isArray(value) ? value : [];
        const firstPage = pageSize != null ? list.slice(0, pageSize) : list;
        return { value: list, pageSize: pageSize ?? recSize, firstPage, totalCount: list.length };
      }

      if (status === 204) {
        return { value: [], pageSize: pageSize ?? recSize, firstPage: [], totalCount: 0 };
      }

      logger.error("fetchOneDriveDocuments non-200/204", { status }, "REDUX");
      return rejectWithValue({
        message: data?.errorMessage ?? `Request failed (${status})`,
      });
    } catch (err) {
      logger.error("fetchOneDriveDocuments failed", err, "REDUX");
      const message =
        err?.response?.data?.message ?? err?.message ?? "Failed to load documents";
      return rejectWithValue({ message });
    }
  }
);

const initialState = {
  value: [],
  loading: false,
  isError: false,
  message: "",
};

const oneDriveListSlice = createSlice({
  name: "oneDriveList",
  initialState,
  reducers: {
    clearOneDriveListError: (state) => {
      state.isError = false;
      state.message = "";
    },
    resetOneDriveList: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOneDriveDocuments.pending, (state) => {
        state.loading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(fetchOneDriveDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.isError = false;
        state.message = "";
        state.value = action.payload?.value ?? [];
      })
      .addCase(fetchOneDriveDocuments.rejected, (state, action) => {
        state.loading = false;
        state.isError = true;
        state.message = action.payload?.message ?? "Unknown error";
        state.value = [];
      });
  },
});

export const { clearOneDriveListError, resetOneDriveList } = oneDriveListSlice.actions;
export default oneDriveListSlice.reducer;
